/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Controller, UseFormReturn, FieldError } from 'react-hook-form';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { Spacer, TextField } from '@qbe/ads-core';
import { CamelCase } from 'type-fest';
import { camelCase } from 'lodash';
import { FormSpacer } from 'components';

type Prefix = 'rep';

type ContactNumberFormData = {
  [key in MName | HName | OName]: string | null;
};

interface Props<T extends ContactNumberFormData> {
  prefix?: 'rep' | '';
  form: UseFormReturn<T>;
}

type MName = CamelCase<`${Prefix | ''} m Phone Number`>;
type HName = CamelCase<`${Prefix | ''} h Phone Number`>;
type OName = CamelCase<`${Prefix | ''} o Phone Number`>;
type OneValidName = CamelCase<`Have One Valid ${Prefix | ''} Number`>;

export default function ContactNumber<T extends ContactNumberFormData>({ form, prefix = '' }: Props<T>) {
  const {
    register,
    formState: { errors },
    control,
  } = form;

  const { getTextContent } = useContent({ context: useContentContext() });

  const mName = camelCase(`${prefix} m Phone Number`) as MName;
  const hName = camelCase(`${prefix} h Phone Number`) as HName;
  const oName = camelCase(`${prefix} o Phone Number`) as OName;
  const oneValidName = camelCase(`Have One Valid ${prefix} Number`) as OneValidName;

  return (
    <>
      <Controller
        name={mName as any}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value!}
            error={!!errors[mName]}
            label={getTextContent('contactNumber.mobile')}
            id={mName}
            data-track={mName}
            data-track-value="mask"
            inputProps={{
              maxLength: 20,
            }}
            helperText={errors[mName] ? (errors[mName] as FieldError)?.message : null}
          />
        )}
      />

      <FormSpacer type="input" />

      <input style={{ display: 'none' }} type="checkbox" {...register(oneValidName as any)} />
      <Controller
        name={hName as any}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value!}
            error={!!errors[hName]}
            label={getTextContent('contactNumber.home')}
            id={hName}
            data-track={hName}
            data-track-value="mask"
            inputProps={{
              maxLength: 20,
            }}
            helperText={errors[hName] ? (errors[hName] as FieldError)?.message : null}
          />
        )}
      />

      <FormSpacer type="input" />

      <Controller
        name={oName as any}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            value={field.value!}
            error={!!errors[oName]}
            label={getTextContent('contactNumber.other')}
            id={oName}
            data-track={oName}
            data-track-value="mask"
            inputProps={{
              maxLength: 20,
            }}
            helperText={errors[oName] ? (errors[oName] as FieldError)?.message : null}
          />
        )}
      />
    </>
  );
}
